import { useEffect, useState } from 'react';
import { Button, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { NachversandKind, StudyGroup } from '@prisma/client';
import type { RetrievalResDto } from '@web/common';
import { BaseModal } from '@web/common-ui';
import { RetrievalStore } from '../../../store/RetrievalStore';
import {
  ACTIONS,
  type ActionKind,
  type Pending,
} from './BdmRetrievalsSection.helpers';
import { BdmTable } from './BdmTable';
import { Part2Table } from './Part2Table';

// Zwei Quellen: bdm-state (studyGroup, BDM-Outcome, Ausfüllstatus – bdmTreatment bleibt
// verblindet) + retrievals (Fulfilment-Status + Aktionen).
export const BdmRetrievalsSection = observer(() => {
  const { t } = useTranslation('nachversand');
  const { userId } = useParams();
  const [store] = useState(() => new RetrievalStore());
  const [pending, setPending] = useState<Pending | null>(null);
  const [switchPending, setSwitchPending] = useState(false);
  const [opened, { open, close }] = useDisclosure(false);

  useEffect(() => {
    if (userId) {
      store.load(userId);
    }
  }, [userId, store]);

  const ask = (retrieval: RetrievalResDto, kind: ActionKind) => {
    setPending({ kind, id: retrieval.id });
    open();
  };

  const askSwitchToVoucher = () => {
    setSwitchPending(true);
    open();
  };

  const confirm = () => {
    if (!userId) {
      return;
    }
    if (switchPending) {
      store.switchBdm1SampleToVoucher(userId);
    } else if (pending) {
      store.updateStatus(userId, pending.id, ACTIONS[pending.kind].nextStatus);
    }
    close();
    setPending(null);
    setSwitchPending(false);
  };

  const cancel = () => {
    close();
    setPending(null);
    setSwitchPending(false);
  };

  const studyGroup = store.bdmState?.studyGroup ?? null;
  const isGroup1 = studyGroup === StudyGroup.GROUP_1;

  const teil2 = store.retrievals
    .filter((r) => r.nachversandKind === NachversandKind.PART2)
    .sort((a, b) => a.createdAt.getTime() - b.createdAt.getTime());

  const groupLabel =
    studyGroup === StudyGroup.GROUP_1
      ? t('Panel.Group.Group1')
      : studyGroup === StudyGroup.GROUP_2
        ? t('Panel.Group.Group2')
        : null;

  const modalKind = pending?.kind ?? 'dispatch';
  const modal = switchPending
    ? {
        title: t('Panel.Modal.SwitchToVoucher.Title'),
        desc: t('Panel.Modal.SwitchToVoucher.Desc'),
      }
    : {
        title: t(`Panel.Modal.${ACTIONS[modalKind].modalKey}.Title`),
        desc: t(`Panel.Modal.${ACTIONS[modalKind].modalKey}.Desc`),
      };

  const nothingToShow =
    !store.loadFailed && !isGroup1 && teil2.length === 0 && !store.isLoading;

  // Die Abschlusslinie rahmt die Tabellen ein — unter einem leeren Panel oder
  // einer Fehlermeldung hätte sie nichts abzuschliessen.
  const hasContent = isGroup1 || teil2.length > 0;

  return (
    <Stack
      gap="32px"
      pb={hasContent ? '24px' : undefined}
      style={hasContent ? { borderBottom: '1px solid #e4e2dc' } : undefined}
    >
      {store.loadFailed && (
        <Stack gap="12px" align="flex-start">
          <Text c="red" size="14px">
            {t('Panel.LoadError')}
          </Text>
          <Button
            variant="light"
            size="xs"
            onClick={() => userId && store.load(userId)}
          >
            {t('Panel.Retry')}
          </Button>
        </Stack>
      )}

      {groupLabel && (
        <Text size="13px" c="dimmed">
          {groupLabel}
        </Text>
      )}

      {isGroup1 && (
        <BdmTable
          retrievals={store.retrievals}
          bdmState={store.bdmState}
          onAction={ask}
          onSwitchToVoucher={askSwitchToVoucher}
        />
      )}

      {teil2.length > 0 && <Part2Table retrievals={teil2} onAction={ask} />}

      {nothingToShow && (
        <Text c="dimmed" size="14px">
          {t('Panel.Empty')}
        </Text>
      )}

      <BaseModal
        opened={opened}
        onClose={cancel}
        title={modal.title}
        description={modal.desc}
        withCloseButton
        buttons={[
          <Button key="confirm" onClick={confirm}>
            {t('Panel.Modal.Confirm')}
          </Button>,
          <Button key="cancel" variant="subtle" onClick={cancel}>
            {t('Panel.Modal.Cancel')}
          </Button>,
        ]}
      />
    </Stack>
  );
});
